// File: frontend/src/components/Pipeline/OpportunityCard.jsx
// Enhanced opportunity card for the Kanban pipeline with better visual design

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  UserIcon,
  CalendarIcon,
  PencilIcon,
  EyeIcon,
  ClockIcon,
  TagIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { getStagePlaybook } from './stagePlaybook';

const OpportunityCard = ({
  opportunity,
  onEdit,
  stageColor = 'text-gray-600',
  isDragging = false
}) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sem data';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const getDaysUntilClose = (expectedCloseDate) => {
    if (!expectedCloseDate) return null;
    const today = new Date();
    const closeDate = new Date(expectedCloseDate);
    const diffTime = closeDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysUntilClose = getDaysUntilClose(opportunity.expected_close_date);
  const isOverdue = daysUntilClose !== null && daysUntilClose < 0;
  const playbook = getStagePlaybook(opportunity.stage);

  const navigate = useNavigate();
  const openDetail = (e) => {
    e.stopPropagation();
    navigate(`/opportunities/${opportunity.id}`);
  };

  return (
    <div 
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 cursor-move hover:shadow-md transition-all duration-200 ${
        isDragging ? 'rotate-2 shadow-lg ring-2 ring-indigo-500 ring-opacity-50' : ''
      }`}
    >
      <div className="space-y-2">
        {/* Title and Company */}
        <div>
          <h4
            onClick={openDetail}
            className="font-medium text-sm text-gray-900 dark:text-gray-100 line-clamp-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
            title="Ver detalhes"
          >
            {opportunity.title}
          </h4>
          {opportunity.company?.name && (
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-300 mt-1">
              <BuildingOfficeIcon className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{opportunity.company.name}</span>
            </div>
          )}
        </div>
        
        {/* Value and Probability */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
            {formatCurrency(opportunity.value)}
          </span>
          <div className="flex items-center space-x-1">
            <div className="w-8 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  opportunity.probability >= 75 ? 'bg-green-500' :
                  opportunity.probability >= 50 ? 'bg-yellow-500' :
                  opportunity.probability >= 25 ? 'bg-orange-500' : 'bg-red-500'
                }`}
                style={{ width: `${opportunity.probability}%` }}
              ></div>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {opportunity.probability}%
            </span>
          </div>
        </div>
        
        {/* User and Source */}
        <div className="flex items-center justify-between">
          {opportunity.user && (
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
              <UserIcon className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">
                {opportunity.user.first_name} {opportunity.user.last_name?.charAt(0)}.
              </span>
            </div>
          )}
          
          {opportunity.source && (
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <TagIcon className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{opportunity.source}</span>
            </div>
          )}
        </div>
        
        {/* Próxima ação sugerida (playbook da etapa) */}
        {playbook && (
          <div
            onClick={openDetail}
            className="flex items-start text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40"
            title="Ver playbook da etapa"
          >
            <LightBulbIcon className="h-3.5 w-3.5 mr-1 mt-px flex-shrink-0" />
            <span className="line-clamp-2">{playbook.hint}</span>
          </div>
        )}

        {/* Date and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center text-xs">
            <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className={`truncate ${
              isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'
            }`}>
              {formatDate(opportunity.expected_close_date)}
            </span>
            {daysUntilClose !== null && (
              <span className={`ml-1 ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                ({daysUntilClose > 0 ? `+${daysUntilClose}` : daysUntilClose}d)
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(opportunity);
              }}
              className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Editar"
            >
              <PencilIcon className="h-3 w-3" />
            </button>
            <button
              onClick={openDetail}
              className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Ver detalhes"
            >
              <EyeIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityCard;